import React, { useEffect, useState } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';
import { Card } from 'primereact/card';
import { useTranslation } from 'react-i18next';
import { rtuReachabilityService } from '../../services/rtuReachabilityService';
import { RtuReachabilityResponse } from '../../types/RtuReachability';
import { RtuSupportedRange } from '../../types/RtuStatus';
import {
  formatCheckSuccessRatePercent,
  formatLatestIntervalPercent,
  getLatestIntervalChecks,
  REACHABILITY_INFO_TOOLTIP_DEFAULT,
} from '../../utils/reachabilityDisplay';
import { SkeletonBlock } from '../common/Skeleton/Skeleton';

const ACTIVITY_RANGE_PRESETS: Array<{ range: RtuSupportedRange; label: string }> = [
  { range: '24h', label: '24H' },
  { range: '7d', label: '7D' },
  { range: '30d', label: '30D' },
];

export const RtuReachabilityActivityCard = ({ rtuId }: { rtuId: number }) => {
  const { t } = useTranslation();
  const [range, setRange] = useState<RtuSupportedRange>('24h');
  const [data, setData] = useState<RtuReachabilityResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await rtuReachabilityService.fetch({ range, rtuId });
        if (active) {
          setData(response);
        }
      } catch (loadError: any) {
        if (active) {
          setData(null);
          setError(
            loadError?.response?.data?.message ||
              loadError?.message ||
              t('dashboard.reachability.errors.loadFailed', {
                defaultValue: 'Unable to load fleet reachability.',
              })
          );
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };
    void run();
    return () => {
      active = false;
    };
    // `t` deliberately excluded - it is only used for a failure-message
    // string, and including it here re-fires this fetch on every unrelated
    // Redux/i18next re-render (see src/locales/i18n.ts).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, rtuId]);

  const summary = data?.summary;
  const pointsCount = data?.points?.length ?? 0;
  const latestIntervalChecks = getLatestIntervalChecks(data);
  const reachabilityTitle = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
      {t('dashboard.page.cards.reachability', { defaultValue: 'Reachability' })}
      <Tooltip
        title={t('dashboard.reachability.infoTooltip', {
          defaultValue: REACHABILITY_INFO_TOOLTIP_DEFAULT,
        })}
        arrow
      >
        <InfoOutlinedIcon
          data-testid="reachability-info-tooltip"
          fontSize="inherit"
          color="action"
          aria-label={t('dashboard.reachability.infoTooltipAria', {
            defaultValue: 'Reachability explanation',
          })}
          sx={{ cursor: 'help', fontSize: '0.95rem' }}
        />
      </Tooltip>
    </span>
  );

  return (
    <Card title={reachabilityTitle} className="info-card compact-card flat-card">
      <div className="dashboard-reachability__chip-group" style={{ marginBottom: '0.5rem' }}>
        {ACTIVITY_RANGE_PRESETS.map((preset) => (
          <button
            key={preset.range}
            type="button"
            className={`dashboard-reachability__chip${range === preset.range ? ' is-active' : ''}`}
            onClick={() => setRange(preset.range)}
          >
            {preset.label}
          </button>
        ))}
      </div>
      {error ? <div className="rtu-activity-card__state rtu-activity-card__state--error">{error}</div> : null}
      <div className="rtu-activity-summary-grid">
        <div className="rtu-activity-summary-tile">
          <span>
            {t('dashboard.reachability.kpi.successRate', { defaultValue: 'Check Success Rate' })}
          </span>
          <strong data-testid="rtu-reachability-success-rate">
            {loading && !summary ? (
              <SkeletonBlock width="2rem" height="0.9rem" style={{ display: 'inline-block' }} />
            ) : (
              formatCheckSuccessRatePercent(summary?.successRatePercent)
            )}
          </strong>
        </div>
        <div className="rtu-activity-summary-tile">
          <span>
            {t('dashboard.reachability.kpi.latestRate', { defaultValue: 'Latest Interval' })}
          </span>
          <strong data-testid="rtu-reachability-latest-interval">
            {loading && !summary ? (
              <SkeletonBlock width="2rem" height="0.9rem" style={{ display: 'inline-block' }} />
            ) : (
              formatLatestIntervalPercent(summary?.latestSuccessRatePercent, latestIntervalChecks)
            )}
          </strong>
        </div>
        <div className="rtu-activity-summary-tile">
          <span>
            {t('dashboard.reachability.kpi.totalChecks', { defaultValue: 'Checks Performed' })}
          </span>
          <strong data-testid="rtu-reachability-total-checks">
            {loading && !summary ? (
              <SkeletonBlock width="2rem" height="0.9rem" style={{ display: 'inline-block' }} />
            ) : (
              summary?.totalChecks ?? 0
            )}
          </strong>
        </div>
      </div>
      <div className="rtu-activity-card__state">
        {loading ? (
          <SkeletonBlock width="14rem" height="0.85rem" style={{ display: 'inline-block' }} />
        ) : (
          t('dashboard.reachability.summary.bucketSummary', {
            defaultValue:
              '{{count}} intervals loaded; {{success}} successful and {{failure}} not fully successful checks.',
            count: pointsCount,
            success: summary?.successCount ?? 0,
            failure: summary?.failureCount ?? 0,
          })
        )}
      </div>
    </Card>
  );
};

export default RtuReachabilityActivityCard;
